/**
 * @author       david.loehr@zumper.com (David Loehr)
 * @copyright    Copyright (c) 2023, Zumper
 */

import type { Location, LocationDescriptorObject } from 'history'
import type { LinkProps } from 'react-router-dom'

export const PREVIOUS_LOCATION = 'previousLocation'

/**
 * Adds the current location to the history state when the link is clicked. For example:
 *
 * 1. User is on /the-first-page?some=query#or-hash
 * 2. User clicks the link: `<Link to={trackPreviousLocationInHistoryState('/next/page')}>Next</Link>`
 * 3. On that next page, the `location` object (in route props or `useLocation()`, etc.) will look
 *    like:
 *    ```ts
 *    {
 *      pathname: '/next/page',
 *      search: '',
 *      ...
 *      state: {
 *        previousLocation: {
 *          hash: '#or-hash',
 *          pathname: '/the-first-page',
 *          search: '?some=query',
 *        }
 *      },
 *    }
 *    ```
 *
 * @param initialTo
 * @example
 * <Link to={trackPreviousLocationInHistoryState('/somewhere')}>Allons-y</Link>
 */
export function trackPreviousLocationInHistoryState(
  initialTo: undefined
): undefined
export function trackPreviousLocationInHistoryState<
  CurrentLocationState = unknown,
  ToLocationState = unknown,
>(
  initialTo: LinkProps<ToLocationState>['to']
): (location: Location<CurrentLocationState>) => LocationDescriptorObject<
  (ToLocationState extends undefined | null ? {} : ToLocationState) & {
    [key in typeof PREVIOUS_LOCATION]: Location<CurrentLocationState>
  }
>
export function trackPreviousLocationInHistoryState<
  CurrentLocationState = unknown,
  ToLocationState = unknown,
>(
  initialTo: LinkProps<ToLocationState>['to'] | undefined
):
  | ((location: Location<CurrentLocationState>) => LocationDescriptorObject<
      (ToLocationState extends undefined | null ? {} : ToLocationState) & {
        [key in typeof PREVIOUS_LOCATION]: Location<CurrentLocationState>
      }
    >)
  | undefined
export function trackPreviousLocationInHistoryState<
  CurrentLocationState = unknown,
  ToLocationState = unknown,
>(
  initialTo: LinkProps<ToLocationState>['to'] | undefined
):
  | ((location: Location<CurrentLocationState>) => LocationDescriptorObject<
      (ToLocationState extends undefined | null ? {} : ToLocationState) & {
        [key in typeof PREVIOUS_LOCATION]: Location<CurrentLocationState>
      }
    >)
  | undefined {
  return initialTo === undefined
    ? undefined
    : (location) => {
        const to =
          // @ts-expect-error react-router-dom's types assume CurrentLocationState and ToLocationState are the same type
          typeof initialTo === 'function' ? initialTo(location) : initialTo
        const toObject =
          typeof to === 'string' ? new URL(to, 'http://example.invalid/') : to
        return {
          hash: toObject.hash,
          pathname: toObject.pathname,
          search: toObject.search,
          // @ts-expect-error state doesn't exist on URL
          state: { ...toObject.state, [PREVIOUS_LOCATION]: location },
        }
      }
}
